/**
 * @license Copyright (c) Microsoft Corporation.  All rights reserved.
 */
import { HtmlAttributes } from "../Constants/HtmlAttributes";
var ValidationUtils = /** @class */ (function () {
    function ValidationUtils() {
    }
    /**
     * Marks the input as invalid and sets the given error message.
     * @param input the target input
     * @param errorMessage error message to be set
     */
    ValidationUtils.setInputInvalid = function (input, errorMessage) {
        input.setCustomValidity(errorMessage);
    };
    /**
     * Marks the input as valid and removes any set error message.
     * @param input the target input
     */
    ValidationUtils.setInputValid = function (input) {
        input.setCustomValidity("");
    };
    /**
     * Marks the given input as invalid
     * and adds the corresponding accessible validation error element to the form.
     * @param form the form with the input
     * @param input the target input
     * @param errorMessage error message to be set
     * @param labelForId value of the 'for' attribute of the target label for the input
     * @param forceVisibleError visible error summary
     */
    ValidationUtils.addValidationError = function (form, input, errorMessage, labelForId, forceVisibleError) {
        if (labelForId === void 0) { labelForId = null; }
        if (forceVisibleError === void 0) { forceVisibleError = false; }
        ValidationUtils.setInputInvalid(input, errorMessage);
        ValidationUtils.setValidationErrorElementForInput(form, input, errorMessage, labelForId, forceVisibleError);
    };
    /**
     * Sets the given input to a valid state
     * and removes the corresponding accessible validation error element from the form.
     * @param form the form with the input
     * @param input the target input
     */
    ValidationUtils.removeValidationError = function (form, input) {
        ValidationUtils.setInputValid(input);
        // remove error element from errors container
        var errorsContainer = ValidationUtils.findValidationErrorsContainer(form);
        if (errorsContainer) {
            var errorElement = ValidationUtils.findValidationErrorElement(errorsContainer, ValidationUtils.getErrorElementId(input));
            if (errorElement) {
                errorsContainer.removeChild(errorElement);
            }
        }
        input.removeAttribute(HtmlAttributes.ariaLabelledBy);
        input.removeAttribute(HtmlAttributes.ariaInvalid);
    };
    ValidationUtils.attachChangeEventHandler = function (input, validator) {
        input.addEventListener("change", validator, false);
    };
    ValidationUtils.attachInvalidEventHandler = function (input, validator) {
        input.addEventListener("invalid", validator, false);
    };
    /**
     * Constructs an id of the accessible validation error element for the given input.
     * @param input
     */
    ValidationUtils.getErrorElementId = function (input) {
        return "error_".concat(input.id);
    };
    ValidationUtils.findValidationErrorsContainer = function (form) {
        return form.querySelector("#".concat(ValidationUtils.validationErrorsContainerId));
    };
    ValidationUtils.findValidationErrorElement = function (errorsContainer, id) {
        return errorsContainer.querySelector("#".concat(id));
    };
    ValidationUtils.getOrCreateValidationErrorsContainer = function (form) {
        var errorsContainer = ValidationUtils.findValidationErrorsContainer(form);
        if (!errorsContainer) {
            errorsContainer = document.createElement("div");
            errorsContainer.id = ValidationUtils.validationErrorsContainerId;
            form.insertBefore(errorsContainer, form.firstChild);
        }
        return errorsContainer;
    };
    /**
     * Gets an existing accessible error element for the input if present.
     * Otherwise, creates a new accessible error element and adds it to the form.
     * @param form the target form for the error element
     * @param input the input for the error element
     * @param errorElementMessage the error message for the error element
     * @param forceVisibleError force visible error
     */
    ValidationUtils.getOrCreateValidationErrorElement = function (form, input, errorElementMessage, forceVisibleError) {
        var errorsContainer = ValidationUtils.getOrCreateValidationErrorsContainer(form);
        var errorElementId = ValidationUtils.getErrorElementId(input);
        var errorElement = errorsContainer.querySelector("#".concat(errorElementId));
        if (!errorElement) {
            errorElement = document.createElement("p");
            errorElement.id = errorElementId;
            errorElement.setAttribute("role", "alert");
            if (!forceVisibleError) {
                errorElement.hidden = true;
            }
            errorElement.textContent = errorElementMessage;
            errorsContainer.insertBefore(errorElement, errorsContainer.firstChild);
        }
        return errorElement;
    };
    /**
     * Constructs an error message for the input's accessible error element by
     * concatenating it with the text content of the label for the input.
     * If no label is found, the same error message is returned.
     * @param form the target form for the error element
     * @param errorMessage the original error message before concatenation
     * @param labelForId value of the 'for' attribute of the target label for the input
     */
    ValidationUtils.getValidationErrorElementMessageForInput = function (form, errorMessage, labelForId) {
        var labelForInput = form.querySelector("label[for=\"".concat(labelForId, "\"]"));
        if (labelForInput) {
            return "".concat(labelForInput.textContent, ": ").concat(errorMessage);
        }
        return errorMessage;
    };
    /**
     * Creates a new accessible validation error element or updates an existing one.
     * Also sets the corresponding attributes for the given input.
     * @param form the form with the input
     * @param input the target input
     * @param errorMessage error message to be set
     * @param labelForId value of the 'for' attribute of the target label for the input, if none specified, input.id is used
     * @param forceVisibleError force error message visibility
     */
    ValidationUtils.setValidationErrorElementForInput = function (form, input, errorMessage, labelForId, forceVisibleError) {
        if (labelForId === void 0) { labelForId = null; }
        var errorElementMessage = ValidationUtils.getValidationErrorElementMessageForInput(form, errorMessage, labelForId ? labelForId : input.id);
        var errorElement = ValidationUtils.getOrCreateValidationErrorElement(form, input, errorElementMessage, forceVisibleError);
        input.setAttribute(HtmlAttributes.ariaLabelledBy, errorElement.id);
        input.setAttribute(HtmlAttributes.ariaInvalid, "true");
    };
    ValidationUtils.validationErrorsContainerId = "validation-summary";
    return ValidationUtils;
}());
export { ValidationUtils };
